import {
  useProbeCatalogShopCount,
  useTriggerCatalogFullSync,
} from "@/api/super-admin/use-super-admin-catalog-sync";
import {
  useReactivateSuperAdminShop,
  useRefreshSuperAdminShopStats,
} from "@/api/super-admin/use-super-admin-shops";
import { Button } from "@babackapp/ui/components/button";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { formatRelative } from "./shop-shared";
import type { WatchItem } from "./watch-list";

const LABELS: Record<WatchItem["reason"], string> = {
  "catalog-gap": "Catalogue incomplet",
  "activity-drop": "Activité en chute",
  "inactive-but-used": "Désactivée mais utilisée",
  "stale-stats": "Stats en retard",
};

function since(ts: number | null): string {
  return ts ? formatRelative(ts) : "jamais";
}

/** Why the shop is listed, in figures. */
function Explanation({ item }: { item: WatchItem }) {
  switch (item.reason) {
    case "catalog-gap":
      return (
        <>
          {item.loaded.toLocaleString("fr-FR")} /{" "}
          {item.expected.toLocaleString("fr-FR")} produits chargés (
          {Math.round((item.loaded / item.expected) * 100)} %) · comptage
          Shopify {since(item.probedAt)}. Sizing et le repli sur les produits
          supprimés lisent ce catalogue.
        </>
      );
    case "activity-drop":
      return (
        <>
          {item.returns30d} retours sur 30 j, aucun sur 7 j · dernier retour{" "}
          {since(item.lastActivityAt)}. Portail cassé, désinstallé, ou
          saisonnalité ?
        </>
      );
    case "inactive-but-used":
      return (
        <>
          Boutique désactivée alors que {item.returns7d} retour
          {item.returns7d > 1 ? "s" : ""} sur 7 j. Réactiver remet isActive et
          la synchro des expéditions.
        </>
      );
    case "stale-stats":
      return (
        <>
          Stats calculées {since(item.statsComputedAt)} : le cron horaire l'a
          manquée (voir le journal, catégorie Jobs).
        </>
      );
  }
}

function Actions({
  item,
  onChanged,
}: {
  item: WatchItem;
  onChanged: () => void;
}) {
  const probe = useProbeCatalogShopCount();
  const fullSync = useTriggerCatalogFullSync();
  const refreshStats = useRefreshSuperAdminShopStats();
  const reactivate = useReactivateSuperAdminShop();
  const [message, setMessage] = useState<string | null>(null);
  const path = { params: { path: { shopId: item.shopId } } };

  async function run(action: () => Promise<unknown>, done: string) {
    setMessage(null);
    try {
      await action();
      setMessage(done);
      onChanged();
    } catch {
      setMessage("Échec, voir le journal");
    }
  }

  const isPending =
    probe.isPending ||
    fullSync.isPending ||
    refreshStats.isPending ||
    reactivate.isPending;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {item.reason === "catalog-gap" && (
        <>
          <Button
            size="sm"
            variant="outline"
            disabled={isPending}
            title="Recompte les produits côté Shopify et côté base"
            onClick={() =>
              run(async () => {
                await probe.mutateAsync(path);
                await refreshStats.mutateAsync(path);
              }, "Recompté")
            }
          >
            Recompter
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={isPending}
            title="Relance une synchro complète du catalogue (file CATALOG_FULL_SYNC)"
            onClick={() =>
              run(
                () => fullSync.mutateAsync(path),
                "Synchro lancée : recompter dans quelques minutes",
              )
            }
          >
            Resynchroniser
          </Button>
        </>
      )}
      {item.reason === "stale-stats" && (
        <Button
          size="sm"
          variant="outline"
          disabled={isPending}
          onClick={() =>
            run(() => refreshStats.mutateAsync(path), "Stats recalculées")
          }
        >
          Recalculer
        </Button>
      )}
      {item.reason === "inactive-but-used" && (
        <Button
          size="sm"
          variant="outline"
          disabled={isPending}
          onClick={() => {
            if (window.confirm(`Réactiver ${item.shopName} ?`)) {
              void run(() => reactivate.mutateAsync(path), "Réactivée");
            }
          }}
        >
          Réactiver
        </Button>
      )}
      {item.reason === "activity-drop" && (
        <Button size="sm" variant="outline" asChild>
          <Link to={`/admin/shops/${item.shopId}/events`}>Voir le journal</Link>
        </Button>
      )}
      {message && (
        <span className="text-xs text-muted-foreground">{message}</span>
      )}
    </div>
  );
}

/**
 * Shops that need a look, each with the figures behind it and what to do.
 * Nothing rendered when there is nothing to show.
 */
export function WatchListPanel({
  items,
  className,
  onChanged,
}: {
  items: WatchItem[];
  className?: string;
  /** After an action: reload the figures the list is built from. */
  onChanged: () => void;
}) {
  if (items.length === 0) return null;

  return (
    <section className={`rounded-lg border bg-background ${className ?? ""}`}>
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">
          Boutiques à surveiller
        </h2>
        <span className="text-xs text-muted-foreground">{items.length}</span>
      </header>
      <ul className="max-h-[32rem] divide-y overflow-y-auto">
        {items.map((item) => (
          <li
            key={`${item.shopId}-${item.reason}`}
            className="space-y-2 px-4 py-3"
          >
            <div className="flex items-center justify-between gap-3">
              <Link
                to={`/admin/shops/${item.shopId}`}
                className="truncate text-sm font-medium text-foreground hover:text-violet-700 hover:underline"
              >
                {item.shopName}
              </Link>
              <span className="shrink-0 rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                {LABELS[item.reason]}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              <Explanation item={item} />
            </p>
            <Actions item={item} onChanged={onChanged} />
          </li>
        ))}
      </ul>
    </section>
  );
}
